import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import {
  DollarSign, Repeat, TrendingUp, Clock, CheckCircle, XCircle,
  Settings, BarChart3, ArrowUpDown, Eye, Loader2, RefreshCw,
  Wallet, Shield, AlertCircle, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  pending: { label: 'En attente', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
  validated: { label: 'Validé', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
  payment_pending: { label: 'Paiement attendu', color: 'bg-orange-500/10 text-orange-400 border-orange-500/20' },
  paid: { label: 'Payé', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  completed: { label: 'Complété', color: 'bg-green-500/10 text-green-400 border-green-500/20' },
  cancelled: { label: 'Annulé', color: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20' },
  rejected: { label: 'Refusé', color: 'bg-red-500/10 text-red-400 border-red-500/20' },
};

const TYPE_LABELS = {
  buy_cad: { label: 'Achat CAD', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  sell_cad: { label: 'Vente CAD', color: 'bg-primary/10 text-primary border-primary/20' },
};

// ─── STATS CARDS ─────────────────────────────────────────────────────
const StatsCards = ({ stats }) => {
  if (!stats) return null;
  const cards = [
    { label: 'Ordres en attente', value: stats.orders?.pending || 0, icon: Clock, accent: 'text-amber-400' },
    { label: 'Ordres complétés', value: stats.orders?.completed || 0, icon: CheckCircle, accent: 'text-green-400' },
    { label: 'Volume CAD', value: `${(stats.volume?.total_cad || 0).toLocaleString()} CAD`, icon: DollarSign, accent: 'text-[#D4AF37]' },
    { label: 'Stock CAD', value: `${(stats.liquidity?.stock_cad || 0).toLocaleString()} CAD`, icon: Wallet, accent: 'text-primary' },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="admin-exchange-stats">
      {cards.map(({ label, value, icon: Icon, accent }) => (
        <div key={label} className="bg-card border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon className={`w-4 h-4 ${accent}`} />
            <span className="text-xs text-muted-foreground">{label}</span>
          </div>
          <p className="font-heading text-xl font-bold">{value}</p>
        </div>
      ))}
    </div>
  );
};

// ─── RATE & SETTINGS PANEL ───────────────────────────────────────────
const SettingsPanel = ({ settings, onRefresh, token }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setForm({
        base_margin_percent: settings.base_margin_percent || 3,
        manual_rate_override: settings.manual_rate_override || 425,
        manual_buy_rate: settings.manual_buy_rate || 430,
        manual_sell_rate: settings.manual_sell_rate || 420,
        use_manual_rate: settings.use_manual_rate || false,
        max_order_cad: settings.max_order_cad || 1000,
        min_order_cad: settings.min_order_cad || 10,
        // Coordonnées de paiement KACOS
        kacos_interac_email: settings.kacos_interac_email || 'fabricengamaleu@gmail.com',
        kacos_interac_security_question: settings.kacos_interac_security_question || 'Transfert',
        kacos_interac_security_answer: settings.kacos_interac_security_answer || 'kacos',
        kacos_mobile_money_phone: settings.kacos_mobile_money_phone || '+237 6 99 48 52 69',
        kacos_mobile_money_name: settings.kacos_mobile_money_name || 'Fabrice Ngamaleu',
        kacos_cash_phone: settings.kacos_cash_phone || '+237 6 97 08 26 81',
      });
    }
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/exchange/admin/settings`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Paramètres mis à jour');
      setEditing(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  const rates = settings?.current_rates || {};

  return (
    <div className="bg-card border rounded-xl p-5 mb-6" data-testid="admin-settings-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold flex items-center gap-2">
          <Settings className="w-4 h-4 text-[#D4AF37]" />
          Taux & Paramètres
        </h3>
        <Button variant="outline" size="sm" onClick={() => setEditing(!editing)}>
          {editing ? 'Annuler' : 'Modifier'}
        </Button>
      </div>

      {/* Current Rates */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="p-3 rounded-lg bg-muted/50 text-center">
          <p className="text-[10px] text-muted-foreground uppercase">Marché</p>
          <p className="font-bold">{rates.market_rate || '—'} XAF</p>
        </div>
        <div className="p-3 rounded-lg bg-emerald-500/5 text-center">
          <p className="text-[10px] text-emerald-600 uppercase">Achat (user paie)</p>
          <p className="font-bold text-emerald-600">{rates.buy_rate || '—'} XAF</p>
        </div>
        <div className="p-3 rounded-lg bg-primary/5 text-center">
          <p className="text-[10px] text-primary uppercase">Vente (user reçoit)</p>
          <p className="font-bold text-primary">{rates.sell_rate || '—'} XAF</p>
        </div>
      </div>

      <div className="text-xs text-muted-foreground mb-2">
        {rates.mode === 'manual'
          ? <span className="text-amber-400">Mode manuel activé | Spread: {rates.spread || '—'} XAF</span>
          : <>Marge: {rates.margin_percent || '—'}% | Spread: {rates.spread || '—'} XAF</>
        }
      </div>

      {editing && (
        <div className="space-y-4 pt-4 border-t">
          {/* Manual rate toggle - at the top for clarity */}
          <label className="flex items-center gap-2 text-sm cursor-pointer p-3 rounded-lg bg-amber-500/5 border border-amber-500/20" data-testid="toggle-manual-rate">
            <input type="checkbox" checked={form.use_manual_rate}
              onChange={(e) => setForm(f => ({ ...f, use_manual_rate: e.target.checked }))}
              className="rounded" />
            <span className="font-medium">Utiliser le taux manuel</span>
            <span className="text-xs text-muted-foreground ml-auto">(désactiver l'API externe)</span>
          </label>

          {/* Manual rates section - shown when manual mode is on */}
          {form.use_manual_rate && (
            <div className="grid grid-cols-3 gap-3 p-3 rounded-lg border border-amber-500/20 bg-amber-500/5">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Taux du marché (XAF/CAD)</label>
                <Input type="number" step="0.01" value={form.manual_rate_override}
                  onChange={(e) => setForm(f => ({ ...f, manual_rate_override: parseFloat(e.target.value) || 0 }))}
                  className="h-9" data-testid="input-manual-market-rate" />
              </div>
              <div>
                <label className="text-xs text-emerald-500 mb-1 block">Taux à l'achat (XAF/CAD)</label>
                <Input type="number" step="0.01" value={form.manual_buy_rate}
                  onChange={(e) => setForm(f => ({ ...f, manual_buy_rate: parseFloat(e.target.value) || 0 }))}
                  className="h-9 border-emerald-500/30" data-testid="input-manual-buy-rate" />
              </div>
              <div>
                <label className="text-xs text-primary mb-1 block">Taux à la vente (XAF/CAD)</label>
                <Input type="number" step="0.01" value={form.manual_sell_rate}
                  onChange={(e) => setForm(f => ({ ...f, manual_sell_rate: parseFloat(e.target.value) || 0 }))}
                  className="h-9 border-primary/30" data-testid="input-manual-sell-rate" />
              </div>
            </div>
          )}

          {/* Auto mode settings - shown when NOT manual */}
          {!form.use_manual_rate && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Marge de base (%)</label>
                <Input type="number" step="0.1" value={form.base_margin_percent}
                  onChange={(e) => setForm(f => ({ ...f, base_margin_percent: parseFloat(e.target.value) || 0 }))}
                  className="h-9" data-testid="input-margin" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Taux fallback (XAF/CAD)</label>
                <Input type="number" value={form.manual_rate_override}
                  onChange={(e) => setForm(f => ({ ...f, manual_rate_override: parseFloat(e.target.value) || 0 }))}
                  className="h-9" data-testid="input-fallback-rate" />
              </div>
            </div>
          )}

          {/* Order limits - always visible */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Min ordre (CAD)</label>
              <Input type="number" value={form.min_order_cad}
                onChange={(e) => setForm(f => ({ ...f, min_order_cad: parseFloat(e.target.value) || 0 }))}
                className="h-9" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Max ordre (CAD)</label>
              <Input type="number" value={form.max_order_cad}
                onChange={(e) => setForm(f => ({ ...f, max_order_cad: parseFloat(e.target.value) || 0 }))}
                className="h-9" />
            </div>
          </div>

          {/* ── Coordonnées de paiement KACOS ── */}
          <div className="pt-4 border-t space-y-3">
            <p className="text-xs font-semibold text-[#D4AF37] uppercase tracking-wider">
              Coordonnées de paiement KACOS
            </p>

            {/* Interac */}
            <div className="p-3 rounded-lg border border-white/10 bg-white/[0.02] space-y-2">
              <p className="text-xs font-medium text-white/60 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] inline-block" />
                Virement Interac
              </p>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Courriel destinataire</label>
                <Input value={form.kacos_interac_email}
                  onChange={(e) => setForm(f => ({ ...f, kacos_interac_email: e.target.value }))}
                  className="h-9" placeholder="ex: votre@email.com"
                  data-testid="input-kacos-interac-email" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Question de sécurité</label>
                  <Input value={form.kacos_interac_security_question}
                    onChange={(e) => setForm(f => ({ ...f, kacos_interac_security_question: e.target.value }))}
                    className="h-9" placeholder="ex: Transfert"
                    data-testid="input-kacos-interac-question" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Mot de passe (réponse)</label>
                  <Input value={form.kacos_interac_security_answer}
                    onChange={(e) => setForm(f => ({ ...f, kacos_interac_security_answer: e.target.value }))}
                    className="h-9" placeholder="ex: kacos"
                    data-testid="input-kacos-interac-answer" />
                </div>
              </div>
            </div>

            {/* Mobile Money */}
            <div className="p-3 rounded-lg border border-white/10 bg-white/[0.02] space-y-2">
              <p className="text-xs font-medium text-white/60 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-400 inline-block" />
                Mobile Money (Orange / MTN)
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Numéro de téléphone</label>
                  <Input value={form.kacos_mobile_money_phone}
                    onChange={(e) => setForm(f => ({ ...f, kacos_mobile_money_phone: e.target.value }))}
                    className="h-9" placeholder="+237 6 XX XX XX XX"
                    data-testid="input-kacos-mm-phone" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Nom du titulaire</label>
                  <Input value={form.kacos_mobile_money_name}
                    onChange={(e) => setForm(f => ({ ...f, kacos_mobile_money_name: e.target.value }))}
                    className="h-9" placeholder="Prénom Nom"
                    data-testid="input-kacos-mm-name" />
                </div>
              </div>
            </div>

            {/* Cash */}
            <div className="p-3 rounded-lg border border-white/10 bg-white/[0.02] space-y-2">
              <p className="text-xs font-medium text-white/60 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />
                Espèces (Cash)
              </p>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Numéro de contact</label>
                <Input value={form.kacos_cash_phone}
                  onChange={(e) => setForm(f => ({ ...f, kacos_cash_phone: e.target.value }))}
                  className="h-9" placeholder="+237 6 XX XX XX XX"
                  data-testid="input-kacos-cash-phone" />
              </div>
            </div>
          </div>

          <Button className="w-full bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold"
            onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      )}
    </div>
  );
};

// ─── LIQUIDITY PANEL ─────────────────────────────────────────────────
const LiquidityPanel = ({ settings, onRefresh, token }) => {
  const [editing, setEditing] = useState(false);
  const [cadVal, setCadVal] = useState('');
  const [xafVal, setXafVal] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setCadVal(settings.stock_cad || 0);
      setXafVal(settings.stock_xaf || 0);
    }
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/exchange/admin/liquidity`,
        { stock_cad: parseFloat(cadVal) || 0, stock_xaf: parseFloat(xafVal) || 0 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Liquidité mise à jour');
      setEditing(false);
      onRefresh();
    } catch (err) {
      toast.error('Erreur');
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-card border rounded-xl p-5 mb-6" data-testid="admin-liquidity-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold flex items-center gap-2">
          <Wallet className="w-4 h-4 text-[#D4AF37]" />
          Liquidité KACOS
        </h3>
        <Button variant="outline" size="sm" onClick={() => setEditing(!editing)}>
          {editing ? 'Annuler' : 'Ajuster'}
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 rounded-lg bg-muted/50">
          <p className="text-[10px] text-muted-foreground uppercase">Stock CAD</p>
          {editing ? (
            <Input type="number" value={cadVal} onChange={(e) => setCadVal(e.target.value)}
              className="h-8 mt-1" data-testid="input-stock-cad" />
          ) : (
            <p className="font-bold text-lg">{(settings?.stock_cad || 0).toLocaleString()} CAD</p>
          )}
        </div>
        <div className="p-3 rounded-lg bg-muted/50">
          <p className="text-[10px] text-muted-foreground uppercase">Stock XAF</p>
          {editing ? (
            <Input type="number" value={xafVal} onChange={(e) => setXafVal(e.target.value)}
              className="h-8 mt-1" data-testid="input-stock-xaf" />
          ) : (
            <p className="font-bold text-lg">{(settings?.stock_xaf || 0).toLocaleString()} XAF</p>
          )}
        </div>
      </div>
      {editing && (
        <Button className="w-full mt-3 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold"
          onClick={handleSave} disabled={saving}>
          {saving ? 'Enregistrement...' : 'Mettre à jour la liquidité'}
        </Button>
      )}
    </div>
  );
};

// ─── ORDER DETAIL MODAL ──────────────────────────────────────────────
const OrderDetailModal = ({ order, open, onClose, token, onRefresh }) => {
  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  if (!order) return null;

  const status = STATUS_STYLES[order.status] || { label: order.status, color: 'bg-zinc-500/10 text-zinc-400' };
  const type = TYPE_LABELS[order.order_type] || { label: order.order_type, color: '' };
  const canCancel = !['completed', 'cancelled', 'rejected'].includes(order.status);

  const handleAction = async (action) => {
    try {
      await axios.post(`${API}/exchange/admin/orders/${order.id}/${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Action effectuée');
      onRefresh();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleCancelConfirm = async () => {
    if (!cancelReason.trim()) { toast.error('Veuillez indiquer une raison'); return; }
    setCancelling(true);
    try {
      await axios.post(`${API}/exchange/admin/orders/${order.id}/cancel`,
        { reason: cancelReason.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ordre annulé');
      setCancelDialog(false);
      setCancelReason('');
      onRefresh();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="order-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowUpDown className="w-5 h-5 text-[#D4AF37]" />
            Ordre #{order.id?.slice(0, 8)}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="flex items-center gap-2">
            <Badge className={`${type.color} border text-xs`}>{type.label}</Badge>
            <Badge className={`${status.color} border text-xs`}>{status.label}</Badge>
            {order.expires_at && order.status === 'payment_pending' && (
              <span className="text-[10px] text-orange-400 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Expire : {new Date(order.expires_at).toLocaleString('fr-FR')}
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><p className="text-muted-foreground text-xs">Utilisateur</p><p className="font-medium">{order.user_info?.full_name || order.user_name}</p></div>
            <div><p className="text-muted-foreground text-xs">Matricule</p><p className="font-medium">{order.user_info?.matricule || '—'}</p></div>
            <div><p className="text-muted-foreground text-xs">Montant CAD</p><p className="font-bold text-lg">{order.amount_cad} CAD</p></div>
            <div><p className="text-muted-foreground text-xs">Montant XAF</p><p className="font-bold text-lg">{order.amount_xaf?.toLocaleString()} XAF</p></div>
            <div><p className="text-muted-foreground text-xs">Taux appliqué</p><p className="font-medium">{order.rate_applied} XAF/CAD</p></div>
            <div><p className="text-muted-foreground text-xs">Mode paiement</p><p className="font-medium">{order.pay_method || order.payment_method || '—'}</p></div>
            <div><p className="text-muted-foreground text-xs">Mode réception</p><p className="font-medium">{order.receive_method || '—'}</p></div>
            {order.receive_name && <div><p className="text-muted-foreground text-xs">Destinataire</p><p className="font-medium">{order.receive_name}</p></div>}
            {order.receive_phone && <div><p className="text-muted-foreground text-xs">Téléphone destinataire</p><p className="font-medium">{order.receive_phone}</p></div>}
            {order.receive_email && <div><p className="text-muted-foreground text-xs">Email destinataire</p><p className="font-medium">{order.receive_email}</p></div>}
            {order.receive_security_question && <div><p className="text-muted-foreground text-xs">Question sécurité</p><p className="font-medium">{order.receive_security_question}</p></div>}
            {order.receive_security_answer && <div><p className="text-muted-foreground text-xs">Réponse sécurité</p><p className="font-medium">{order.receive_security_answer}</p></div>}
            {order.cancel_reason && <div className="col-span-2"><p className="text-muted-foreground text-xs">Raison annulation</p><p className="font-medium text-red-400">{order.cancel_reason}</p></div>}
            {order.user_info?.email && <div className="col-span-2"><p className="text-muted-foreground text-xs">Email utilisateur</p><p className="font-medium">{order.user_info.email}</p></div>}
          </div>

          {/* History */}
          {order.status_history?.length > 0 && (
            <div className="pt-3 border-t">
              <p className="text-xs text-muted-foreground mb-2 font-medium">Historique</p>
              <div className="space-y-1.5 max-h-32 overflow-y-auto">
                {order.status_history.map((h, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className="text-muted-foreground">{new Date(h.timestamp).toLocaleString('fr-FR')}</span>
                    <span className="font-medium">{h.note}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-3 border-t">
            {order.status === 'pending' && (
              <>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1" onClick={() => handleAction('validate')} data-testid="btn-validate-order">
                  <CheckCircle className="w-4 h-4 mr-1" /> Valider
                </Button>
                <Button size="sm" variant="destructive" className="flex-1" onClick={() => handleAction('reject')} data-testid="btn-reject-order">
                  <XCircle className="w-4 h-4 mr-1" /> Refuser
                </Button>
              </>
            )}
            {order.status === 'payment_pending' && (
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white flex-1" onClick={() => handleAction('confirm-payment')} data-testid="btn-confirm-payment">
                <DollarSign className="w-4 h-4 mr-1" /> Confirmer paiement reçu
              </Button>
            )}
            {order.status === 'validated' && (
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white flex-1" onClick={() => handleAction('confirm-payment')} data-testid="btn-confirm-payment-validated">
                <DollarSign className="w-4 h-4 mr-1" /> Confirmer paiement
              </Button>
            )}
            {order.status === 'paid' && (
              <Button size="sm" className="bg-[#D4AF37] hover:bg-[#C4A030] text-black flex-1" onClick={() => handleAction('complete')} data-testid="btn-complete-order">
                <CheckCircle className="w-4 h-4 mr-1" /> Compléter (transfert fait)
              </Button>
            )}
            {canCancel && (
              <Button size="sm" variant="ghost"
                className="text-red-400 hover:text-red-500 hover:bg-red-500/10 border border-red-500/20"
                onClick={() => setCancelDialog(true)}
                data-testid="btn-cancel-order">
                <Trash2 className="w-4 h-4 mr-1" /> Annuler l'ordre
              </Button>
            )}
          </div>
        </div>
      </DialogContent>

      {/* Cancel confirmation dialog */}
      <Dialog open={cancelDialog} onOpenChange={(v) => { setCancelDialog(v); if (!v) setCancelReason(''); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <Trash2 className="w-4 h-4" /> Annuler l'ordre #{order.id?.slice(0, 8)}
            </DialogTitle>
            <DialogDescription>
              Cette action notifiera l'utilisateur. Veuillez indiquer la raison.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Raison de l'annulation (ex: document manquant, erreur de montant...)"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            className="min-h-[80px]"
            data-testid="cancel-reason-input"
          />
          <DialogFooter className="gap-2 flex-row justify-end">
            <Button variant="outline" onClick={() => { setCancelDialog(false); setCancelReason(''); }}
              data-testid="cancel-dialog-close">
              Fermer
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleCancelConfirm}
              disabled={cancelling || !cancelReason.trim()}
              data-testid="cancel-order-confirm"
            >
              {cancelling ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
              Confirmer l'annulation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

// ─── ORDERS TABLE ────────────────────────────────────────────────────
const OrdersTable = ({ orders, token, onRefresh }) => {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');

  const filtered = orders.filter(o => {
    if (filterStatus !== 'all' && o.status !== filterStatus) return false;
    if (filterType !== 'all' && o.order_type !== filterType) return false;
    return true;
  });

  return (
    <div data-testid="admin-orders-table">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-xs text-muted-foreground">Statut:</span>
        {['all', 'pending', 'validated', 'paid', 'completed', 'rejected', 'cancelled'].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)}
            className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
              filterStatus === s ? 'bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30' : 'bg-muted/50 text-muted-foreground border-transparent hover:border-muted-foreground/20'
            }`}>
            {s === 'all' ? 'Tous' : (STATUS_STYLES[s]?.label || s)}
          </button>
        ))}
        <span className="text-xs text-muted-foreground ml-2">Type:</span>
        {['all', 'buy_cad', 'sell_cad'].map(t => (
          <button key={t} onClick={() => setFilterType(t)}
            className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
              filterType === t ? 'bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30' : 'bg-muted/50 text-muted-foreground border-transparent hover:border-muted-foreground/20'
            }`}>
            {t === 'all' ? 'Tous' : (TYPE_LABELS[t]?.label || t)}
          </button>
        ))}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <ArrowUpDown className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>Aucun ordre trouvé</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(order => {
            const status = STATUS_STYLES[order.status] || { label: order.status, color: '' };
            const type = TYPE_LABELS[order.order_type] || { label: order.order_type, color: '' };
            return (
              <div key={order.id}
                className="flex items-center gap-3 bg-card border rounded-xl p-3 hover:border-[#D4AF37]/30 transition-colors cursor-pointer"
                onClick={() => setSelectedOrder(order)}
                data-testid={`order-row-${order.id?.slice(0, 8)}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={`${type.color} border text-[10px]`}>{type.label}</Badge>
                    <span className="text-sm font-semibold">{order.amount_cad} CAD</span>
                    <span className="text-xs text-muted-foreground">@ {order.rate_applied} XAF</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{order.user_info?.full_name || order.user_name}</span>
                    <span>|</span>
                    <span>{new Date(order.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                </div>
                <Badge className={`${status.color} border text-xs`}>{status.label}</Badge>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <Eye className="w-4 h-4" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      <OrderDetailModal
        order={selectedOrder}
        open={!!selectedOrder}
        onClose={() => setSelectedOrder(null)}
        token={token}
        onRefresh={onRefresh}
      />
    </div>
  );
};

// ─── MAIN COMPONENT ──────────────────────────────────────────────────
export const AdminCurrencySection = ({ token }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    const headers = { Authorization: `Bearer ${token}` };
    setLoading(true);
    try {
      const [statsRes, settingsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/exchange/admin/stats`, { headers }),
        axios.get(`${API}/exchange/admin/settings`, { headers }),
        axios.get(`${API}/exchange/admin/orders`, { headers }),
      ]);
      setStats(statsRes.data);
      setSettings(settingsRes.data);
      setOrders(ordersRes.data);
    } catch (err) {
      console.error('Admin exchange fetch error:', err);
    } finally { setLoading(false); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const tabs = [
    { id: 'dashboard', label: 'Tableau de bord', icon: BarChart3 },
    { id: 'orders', label: 'Ordres', icon: ArrowUpDown },
    { id: 'settings', label: 'Paramètres', icon: Settings },
  ];

  return (
    <div data-testid="admin-currency-section">
      {/* Tab Navigation */}
      <div className="flex gap-1 border-b mb-6 overflow-x-auto">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button key={id}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === id
                ? 'border-[#D4AF37] text-[#D4AF37]'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setActiveTab(id)}
            data-testid={`admin-tab-${id}`}
          >
            <Icon className="w-4 h-4" />
            {label}
            {id === 'orders' && stats?.orders?.pending > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-[10px] font-bold">
                {stats.orders.pending}
              </span>
            )}
          </button>
        ))}
        <button className="ml-auto p-2 text-muted-foreground hover:text-foreground" onClick={fetchAll}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading && !stats ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
        </div>
      ) : (
        <>
          {activeTab === 'dashboard' && (
            <div>
              <StatsCards stats={stats} />

              {/* Quick summary */}
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="bg-card border rounded-xl p-4">
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-[#D4AF37]" /> Répartition des ordres
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Achats CAD</span>
                      <span className="font-medium text-emerald-500">{stats?.orders?.buy_cad || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Ventes CAD</span>
                      <span className="font-medium text-primary">{stats?.orders?.sell_cad || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm pt-2 border-t">
                      <span className="text-muted-foreground">Total</span>
                      <span className="font-bold">{stats?.orders?.total || 0}</span>
                    </div>
                  </div>
                </div>
                <div className="bg-card border rounded-xl p-4">
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-[#D4AF37]" /> Taux actuel
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Marché</span>
                      <span className="font-medium">{stats?.rates?.market_rate || '—'} XAF</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Achat (user paie)</span>
                      <span className="font-medium text-emerald-500">{stats?.rates?.buy_rate || '—'} XAF</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Vente (user reçoit)</span>
                      <span className="font-medium text-primary">{stats?.rates?.sell_rate || '—'} XAF</span>
                    </div>
                    <div className="flex justify-between text-sm pt-2 border-t">
                      <span className="text-muted-foreground">Marge</span>
                      <span className="font-bold text-[#D4AF37]">{stats?.rates?.margin_percent || '—'}%</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pending orders quick list */}
              {orders.filter(o => o.status === 'pending').length > 0 && (
                <div className="bg-card border border-amber-500/20 rounded-xl p-4">
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-400" />
                    Ordres en attente ({orders.filter(o => o.status === 'pending').length})
                  </h4>
                  <div className="space-y-2">
                    {orders.filter(o => o.status === 'pending').slice(0, 5).map(o => (
                      <div key={o.id} className="flex items-center justify-between text-sm bg-muted/30 rounded-lg p-2">
                        <div className="flex items-center gap-2">
                          <Badge className={`${TYPE_LABELS[o.order_type]?.color || ''} border text-[10px]`}>
                            {TYPE_LABELS[o.order_type]?.label || o.order_type}
                          </Badge>
                          <span className="font-medium">{o.amount_cad} CAD</span>
                          <span className="text-muted-foreground">{o.user_info?.full_name || o.user_name}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {new Date(o.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                        </span>
                      </div>
                    ))}
                  </div>
                  <Button variant="outline" size="sm" className="w-full mt-3"
                    onClick={() => setActiveTab('orders')}>
                    Voir tous les ordres
                  </Button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'orders' && (
            <OrdersTable orders={orders} token={token} onRefresh={fetchAll} />
          )}

          {activeTab === 'settings' && (
            <div className="grid lg:grid-cols-2 gap-6">
              <SettingsPanel settings={settings} onRefresh={fetchAll} token={token} />
              <LiquidityPanel settings={settings} onRefresh={fetchAll} token={token} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminCurrencySection;
